/**
 * <StageAuditPanel leadId={id} /> — Apex 2.0 Phase 3a.
 *
 * Compact audit log of every auto-stage-move (and "considered but
 * skipped" verdict) for a lead. One-click undo on applied moves.
 *
 * Endpoints:
 *   GET  /api/apex/stage-audit?lead_id=...
 *   POST /api/apex/stage-audit/{audit_id}/revert
 */
import { useCallback, useEffect, useState } from "react";
import { Loader2, GitBranch, Undo2, AlertCircle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

function fmt(iso) {
  try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return iso; }
}

export default function StageAuditPanel({ leadId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!leadId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/apex/stage-audit?lead_id=${leadId}&limit=20`);
      setItems(Array.isArray(data) ? data : []);
    } catch { setItems([]); }
    setLoading(false);
  }, [leadId]);

  useEffect(() => { load(); }, [load]);

  const onRevert = async (id) => {
    if (!window.confirm("Revert this stage move?")) return;
    try {
      await api.post(`/apex/stage-audit/${id}/revert`);
      toast.success("Stage move reverted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Revert failed");
    }
  };

  if (loading) {
    return (
      <div className="card p-4 text-center text-sm text-[var(--ink-3)]" data-testid="stage-audit-loading">
        <Loader2 size={14} className="animate-spin inline mr-2" /> Loading stage history…
      </div>
    );
  }
  if (items.length === 0) return null;  // hide panel entirely if no audit history yet

  return (
    <section className="card p-5" data-testid="stage-audit-panel">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--ink-3)]">
            Apex 2.0 · Phase 3a
          </div>
          <h3 className="font-display font-bold text-lg flex items-center gap-2">
            <GitBranch size={16} className="text-emerald-600" /> Auto-stage history
          </h3>
        </div>
        <span className="text-[10px] font-bold px-2 py-1 rounded-full bg-emerald-100 text-emerald-700">
          {items.filter((a) => a.applied).length} applied · {items.length} total
        </span>
      </div>

      <div className="space-y-2" data-testid="stage-audit-list">
        {items.map((a) => (
          <div key={a.id}
            className={`flex items-start gap-3 p-3 rounded-lg border ${
              a.applied && !a.reverted_at
                ? "border-emerald-200 bg-emerald-50/40"
                : a.reverted_at
                  ? "border-slate-200 bg-slate-50 opacity-70"
                  : "border-amber-200 bg-amber-50/40"
            }`}
            data-testid={`stage-audit-row-${a.id}`}
          >
            <div className="shrink-0 w-8 h-8 rounded-lg flex items-center justify-center text-white"
                 style={{ background: a.applied && !a.reverted_at ? "#10B981" : a.reverted_at ? "#94A3B8" : "#F59E0B" }}>
              {a.applied && !a.reverted_at ? <CheckCircle2 size={14} /> : a.reverted_at ? <Undo2 size={14} /> : <AlertCircle size={14} />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-sm">
                {a.applied && !a.reverted_at
                  ? <>{a.from_stage} → <span className="text-emerald-700">{a.to_stage}</span></>
                  : a.reverted_at
                    ? <span className="line-through opacity-60">{a.from_stage} → {a.to_stage}</span>
                    : <>Suggested: <span className="text-amber-700">{a.suggested_stage}</span> (below threshold)</>}
              </div>
              <div className="text-[11px] text-[var(--ink-3)]">
                {fmt(a.created_at)} · {a.signal_kind} · confidence {Math.round((a.confidence || 0) * 100)}%
                {a.reverted_at ? " · reverted" : ""}
              </div>
              {a.rationale && (
                <div className="text-[11px] text-[var(--ink-2)] italic mt-1 line-clamp-2">"{a.rationale}"</div>
              )}
              {a.signal_text && (
                <div className="text-[10px] text-[var(--ink-3)] mt-1 line-clamp-1 font-mono opacity-70">
                  Signal: {a.signal_text.slice(0, 120)}
                </div>
              )}
            </div>
            {a.applied && !a.reverted_at && (
              <button type="button"
                className="btn-ghost text-xs text-slate-600 shrink-0"
                title="Revert this stage move"
                onClick={() => onRevert(a.id)}
                data-testid={`stage-audit-revert-${a.id}`}>
                <Undo2 size={12} /> Undo
              </button>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}
